import React, { useEffect, useState } from "react";
import { getDashboardStats, getMezziOggi, getScadenze, getContrattiScaduti } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { TrendingUp, Scale, Wallet, AlertTriangle, Clock, MapPin, Truck, AlertCircle, CheckCircle2, FileWarning } from "lucide-react";
import { formatCurrency, formatNumber, STATO_GIRO_COLOR, STATO_GIRO_LABEL } from "../lib/constants";
import { Link } from "react-router-dom";

function BigStat({ icon: Icon, label, value, sub, testid, color = "#1B5E20" }) {
  return (
    <Card data-testid={testid} className="p-5 sm:p-6 bg-white rounded-xl border border-slate-200 shadow-sm card-hover">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</div>
          <div className="font-extrabold mt-2 text-slate-900 leading-none break-words" style={{ fontFamily: "Outfit, sans-serif", fontSize: "clamp(2.25rem, 6vw, 2.75rem)" }}>{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
        </div>
        <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15`, color }}><Icon size={24} /></div>
      </div>
    </Card>
  );
}

const MEZZO_STATO_ICON = {
  PIANIFICATO: Clock,
  IN_CORSO: TrendingUp,
  COMPLETATO: CheckCircle2,
  ANNULLATO: AlertTriangle,
  INATTIVO: AlertCircle,
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [mezziOggi, setMezziOggi] = useState(null);
  const [scadenzeCfg, setScadenzeCfg] = useState(null);
  const [scaduti, setScaduti] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getDashboardStats(), getMezziOggi(), getScadenze(), getContrattiScaduti(10)])
      .then(([s, m, sc, exp]) => { setStats(s); setMezziOggi(m); setScadenzeCfg(sc.config); setScaduti(exp); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-4 text-slate-500" data-testid="dashboard-loading">Caricamento…</div>;
  if (!stats) return <div className="p-4 text-red-600">Errore nel caricamento statistiche</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Dashboard</h1>
        <p className="text-slate-500 text-sm mt-1">{stats.produttori_totali} produttori attivi</p>
      </div>

      {/* Totali */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-3">Totali</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <BigStat testid="stat-visite-totali" icon={TrendingUp} label="Visite totali" value={formatNumber(stats.totali.visite)} color="#1B5E20" />
          <BigStat testid="stat-kg-totali" icon={Scale} label="Kg ritirati" value={formatNumber(stats.totali.kg)} color="#F57C00" />
          <BigStat testid="stat-importo-totale" icon={Wallet} label="Importo totale" value={formatCurrency(stats.totali.importo)} color="#1565C0" />
        </div>
      </section>

      {/* Mese corrente */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-3">Mese corrente</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <BigStat testid="stat-visite-mese" icon={TrendingUp} label="Visite" value={formatNumber(stats.mese_corrente.visite)} color="#1B5E20" />
          <BigStat testid="stat-kg-mese" icon={Scale} label="Kg" value={formatNumber(stats.mese_corrente.kg)} color="#F57C00" />
          <BigStat testid="stat-importo-mese" icon={Wallet} label="Importo" value={formatCurrency(stats.mese_corrente.importo)} color="#1565C0" />
        </div>
      </section>

      {/* Mezzi oggi */}
      {mezziOggi?.mezzi && mezziOggi.mezzi.length > 0 && (
        <section data-testid="mezzi-oggi-section">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Mezzi oggi</h2>
            <Link to="/giro" className="text-xs text-[#1B5E20] font-semibold hover:underline">Vai al giro →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {mezziOggi.mezzi.map((mo) => {
              const StatoIcon = MEZZO_STATO_ICON[mo.stato] || Clock;
              const color = mo.mezzo.colore;
              const km = (mo.kmIniziali != null && mo.kmFinali != null) ? (mo.kmFinali - mo.kmIniziali) : null;
              return (
                <Link key={mo.mezzo.id} to={`/giro?mezzo=${mo.mezzo.id}`} data-testid={`mezzo-oggi-${mo.mezzo.id}`}>
                  <Card className="p-4 bg-white border border-slate-200 card-hover h-full">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: color }}><Truck size={18} /></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 truncate text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>{mo.mezzo.nome}</div>
                        <div className="text-xs text-slate-500 truncate">{mo.mezzo.targa}</div>
                      </div>
                      <Badge style={{ backgroundColor: STATO_GIRO_COLOR[mo.stato], color: "#fff" }} className="text-[10px] flex items-center gap-1">
                        <StatoIcon size={11} />{STATO_GIRO_LABEL[mo.stato]}
                      </Badge>
                    </div>
                    {mo.zone.length > 0 ? (
                      <>
                        <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">Zone assegnate</div>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {mo.zone.map((z, i) => <Badge key={i} variant="outline" className="text-[10px]">{z}</Badge>)}
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div><span className="text-slate-500">Tappe</span><div className="font-bold text-slate-900">{mo.tappe}</div></div>
                          <div><span className="text-slate-500">Ritirati</span><div className="font-bold text-[#1B5E20]">{mo.ritirati}</div></div>
                          {mo.stato === "COMPLETATO" && km != null ? (
                            <div><span className="text-slate-500">Km</span><div className="font-bold text-[#F57C00]">{km}</div></div>
                          ) : (
                            <div><span className="text-slate-500">Km</span><div className="text-slate-300">—</div></div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-slate-400 italic">Nessuna zona assegnata oggi</div>
                    )}
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Contratti — 4 livelli (3 visibili + OK senza badge) */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-3">Contratti in scadenza</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ScadenzaCard color="#C62828" outline label="Scaduti" count={stats.contratti.scaduti} link="/produttori?stato=SCADUTO" testid="scaduti" filled />
          <ScadenzaCard color="#C62828" label={`Allerta rossa ≤${scadenzeCfg?.rossa_giorni || 30}gg`} count={stats.contratti.sotto_soglia_rossa} link="/produttori?stato=URGENTE" testid="rossa" outline />
          <ScadenzaCard color="#F57C00" label={`Allerta gialla ≤${scadenzeCfg?.gialla_giorni || 120}gg`} count={stats.contratti.sotto_soglia_gialla} link="/produttori?stato=IN_SCADENZA" testid="gialla" outline />
        </div>
      </section>

      {/* Push 5: Top 10 contratti più scaduti / in scadenza */}
      {scaduti && scaduti.top && scaduti.top.length > 0 && (
        <section data-testid="contratti-scaduti-widget">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 flex items-center gap-2">
              <FileWarning size={14} className="text-[#C62828]" />
              Contratti più scaduti — Top 10
            </h2>
            <Link to="/produttori?stato=SCADUTO" className="text-xs text-[#C62828] font-semibold hover:underline">
              Vedi tutti ({scaduti.totali.tutti}) →
            </Link>
          </div>
          <Card className="p-2 bg-white border border-slate-200 shadow-sm">
            <ul className="divide-y divide-slate-100">
              {scaduti.top.map((c) => {
                const livColor = c.livello === "SCADUTO" ? "#7F1D1D" : c.livello === "URGENTE" ? "#C62828" : "#F57C00";
                const livLabel = c.livello === "SCADUTO" ? "🚨 SCADUTO" : c.livello === "URGENTE" ? "⚠️ URGENTE" : "⏰ IN SCADENZA";
                return (
                  <li key={c.id}>
                    <Link
                      to={`/produttori/${c.id}`}
                      data-testid={`contratto-scaduto-${c.id}`}
                      className="flex items-center justify-between gap-3 px-3 py-3 hover:bg-slate-50"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 truncate text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>
                          {c.ragioneSociale}
                        </div>
                        <div className="text-xs text-slate-500 truncate flex items-center gap-1 mt-0.5">
                          <MapPin size={11} /> {c.zona || "-"}
                          {c.rinnovoTacito && c.rinnovoTacito !== "NESSUNO" && (
                            <span className="ml-1 text-[10px] text-slate-400">· rinnovo {c.rinnovoTacito}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1 shrink-0">
                        <Badge style={{ backgroundColor: livColor, color: "#fff" }} className="text-[10px]">
                          {livLabel}
                        </Badge>
                        <span className="text-[11px] text-slate-500">
                          {c.giorni != null && c.giorni < 0 ? `${Math.abs(c.giorni)} gg fa` : c.giorni != null ? `tra ${c.giorni} gg` : "—"}
                        </span>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </Card>
        </section>
      )}

      {/* Zone */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-3">Produttori per zona</h2>
        <Card className="p-2 bg-white border border-slate-200 shadow-sm" data-testid="zone-list">
          <ul className="divide-y divide-slate-100">
            {stats.zone.map((z) => (
              <li key={z.zona} className="flex items-center justify-between px-3 py-3">
                <div className="flex items-center gap-2 min-w-0">
                  <MapPin size={16} className="text-[#1B5E20]" />
                  <span className="font-medium text-slate-800 truncate">{z.zona}</span>
                </div>
                <Link to={`/produttori?zona=${encodeURIComponent(z.zona)}`} className="text-sm font-semibold text-[#1B5E20] hover:text-[#144818]" data-testid={`zona-link-${z.zona}`}>{z.count}</Link>
              </li>
            ))}
          </ul>
        </Card>
      </section>
    </div>
  );
}

function ScadenzaCard({ color, label, count, link, testid, outline, filled }) {
  return (
    <Link to={link} data-testid={`scadenza-${testid}`}>
      <Card className={`p-3 bg-white card-hover ${outline ? "border-2" : "border"} `} style={{ borderColor: outline ? color : "#e2e8f0" }}>
        <div className="flex items-center gap-2 mb-1">
          <span className={`h-2.5 w-2.5 rounded-full ${filled ? "" : "border-2 bg-white"}`} style={filled ? { backgroundColor: color } : { borderColor: color }} />
          <div className="text-[10px] uppercase font-semibold tracking-wider truncate" style={{ color }}>{label}</div>
        </div>
        <div className="text-2xl font-bold" style={{ fontFamily: "Outfit, sans-serif", color }}>{count}</div>
      </Card>
    </Link>
  );
}
